/**
 * useSwipeNavigation — horizontal touch-swipe navigation for photo surfaces.
 *
 * Buyers on phones expect to flip through listing photos with a thumb swipe
 * rather than hunting for the small ‹ › arrows (QA note N-03). This hook turns
 * a raw touch gesture into a left/right intent and hands back handler props to
 * spread onto the swipe surface.
 *
 * Deliberate non-goals:
 *   - We never call `preventDefault()`. A property page is taller than the
 *     screen, so hijacking the vertical gesture would break page scrolling —
 *     the single worst thing a photo viewer can do on mobile. Pair the returned
 *     handlers with `touch-action: pan-y` in CSS so the browser can start
 *     scrolling immediately instead of waiting to see what we do.
 *   - We ignore gestures that are mostly vertical, so a scroll that drifts a
 *     little sideways doesn't also flip the photo.
 *
 * Usage:
 *   const { swipeHandlers, consumeSwipeClick } = useSwipeNavigation({
 *     onSwipeLeft: goNext,
 *     onSwipeRight: goPrev,
 *     enabled: images.length > 1,
 *   });
 *   <div {...swipeHandlers} onClick={() => { if (consumeSwipeClick()) return; ... }} />
 */

import { useCallback, useRef } from 'react';

// A gesture has to travel this far horizontally to count. Tapping jitters a few
// pixels on a real finger, so anything shorter is a tap, not a swipe.
export const SWIPE_MIN_DISTANCE_PX = 50;

// ...and the horizontal travel has to beat the vertical travel by this factor.
// A pure page scroll rarely stays perfectly vertical; without this a fast flick
// down the page would also advance the photo.
export const SWIPE_HORIZONTAL_RATIO = 1.5;

const readPoint = (touch) => (touch ? { x: touch.clientX, y: touch.clientY } : null);

const useSwipeNavigation = ({ onSwipeLeft, onSwipeRight, enabled = true }) => {
  const startRef = useRef(null);
  const lastMoveRef = useRef(null);
  // Touch devices synthesise a click after touchend. The gallery uses that
  // click to open the lightbox, so a swipe would both change the photo AND
  // open fullscreen. Flag the swipe so the click handler can swallow it.
  const swipedRef = useRef(false);

  const onTouchStart = useCallback((e) => {
    if (!enabled) return;
    // Multi-touch is not a swipe. `touch-action: pan-y` disables pinch-zoom on
    // the gallery, so a pinch does nothing visible — but its touch events still
    // fire, and measuring finger one's travel would read a spread as a swipe
    // and flip the photo. Bail out rather than guess at the intent.
    if (e.touches && e.touches.length > 1) {
      startRef.current = null;
      lastMoveRef.current = null;
      return;
    }
    const point = readPoint(e.touches?.[0] || e.changedTouches?.[0]);
    if (!point) return;
    startRef.current = point;
    lastMoveRef.current = point;
    swipedRef.current = false;
  }, [enabled]);

  const onTouchMove = useCallback((e) => {
    if (!enabled || !startRef.current) return;
    // A second finger landing mid-gesture cancels it: what began as a drag has
    // become a pinch, and the first finger's remaining travel is not intent.
    if (e.touches && e.touches.length > 1) {
      startRef.current = null;
      lastMoveRef.current = null;
      return;
    }
    const point = readPoint(e.touches?.[0] || e.changedTouches?.[0]);
    if (point) lastMoveRef.current = point;
  }, [enabled]);

  const onTouchEnd = useCallback((e) => {
    const start = startRef.current;
    startRef.current = null;
    if (!enabled || !start) return;

    // `changedTouches` is the authoritative end position (`touches` is already
    // empty by touchend); fall back to the last move for synthetic events.
    const end = readPoint(e.changedTouches?.[0]) || lastMoveRef.current;
    lastMoveRef.current = null;
    if (!end) return;

    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    if (Math.abs(deltaX) < SWIPE_MIN_DISTANCE_PX) return;
    if (Math.abs(deltaX) < Math.abs(deltaY) * SWIPE_HORIZONTAL_RATIO) return;

    swipedRef.current = true;
    // Swiping left drags the current photo off to the left, revealing the next.
    if (deltaX < 0) {
      if (onSwipeLeft) onSwipeLeft();
    } else if (onSwipeRight) {
      onSwipeRight();
    }
  }, [enabled, onSwipeLeft, onSwipeRight]);

  /**
   * Returns true (once) if the click currently being handled was the tail of a
   * swipe, so the caller can skip its own click behaviour.
   */
  const consumeSwipeClick = useCallback(() => {
    if (!swipedRef.current) return false;
    swipedRef.current = false;
    return true;
  }, []);

  return {
    swipeHandlers: { onTouchStart, onTouchMove, onTouchEnd },
    consumeSwipeClick,
  };
};

export default useSwipeNavigation;
